"use client";

import React from "react";
import { CircleDot, X } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@oreme/ui";
import { useSite } from "@/hooks";
import { round } from "@/utils";
import { MissionType, SiteType } from "@/types";
import { SiteSheet } from "@/components";
import { useTranslations } from "next-intl";

type SiteMapLibrePopupProps = {
  site: SiteType;
  missions: MissionType[];
};

function SiteMapLibrePopup({
  site,
  missions,
}: Readonly<SiteMapLibrePopupProps>) {
  const t = useTranslations("site.SiteMapLibrePopup");
  const { resetSite } = useSite();

  const handleClose = () => {
    resetSite();
  };

  return (
    <Card className="min-w-60 p-0">
      <CardHeader className="px-4 py-2">
        <CardTitle className="flex flex-row items-center justify-between">
          <div className="inline-flex gap-2 items-center text-lg">
            <CircleDot size={16} />
            Site {site.site}
          </div>
          <Button
            size="icon"
            onClick={() => {
              handleClose();
            }}
            variant="ghost"
            title={t("close")}
          >
            <X size={16} />
          </Button>
        </CardTitle>
        {site.country ? (
          <CardDescription>{site.country}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="px-4 py-2">
        <dl className="grid grid-cols-12 gap-1 min-w-40 text-sm">
          {site.latitude ? (
            <>
              <dt className="font-semibold col-span-4">{t("latitude")}</dt>
              <dd
                className="col-span-8 truncate text-right"
                title={site.latitude.toString()}
              >
                {round(site.latitude, 2)}
              </dd>
            </>
          ) : null}
          {site.longitude ? (
            <>
              <dt className="font-semibold col-span-4">{t("longitude")}</dt>
              <dd
                className="col-span-8 truncate text-right"
                title={site.longitude.toString()}
              >
                {round(site.longitude, 2)}
              </dd>
            </>
          ) : null}
          {site.altitude ? (
            <>
              <dt className="font-semibold col-span-4">{t("altitude")}</dt>
              <dd
                className="col-span-8 truncate text-right"
                title={site.altitude.toString()}
              >
                {round(site.altitude, 0)} m
              </dd>
            </>
          ) : null}
        </dl>
      </CardContent>
      <CardFooter className="p-4">
        <SiteSheet site={site} missions={missions} />
      </CardFooter>
    </Card>
  );
}

export { SiteMapLibrePopup };
